import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Button, message, Row, Col, Statistic, Space, Table } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../shared/api/AllApi';
import { formatCurrency } from '../../../utils/formatters';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title } = Typography;

const CustomerOrderDetail = () => {
  const { customerId, orderId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [totalWeightOrder, settotalWeightOrder] = useState(0);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(-1);

  const fetchCustomerOrders = useCallback(async () => {
    try {
      const response = await apiClient.get(`/customers/${customerId}/orders`);      
      setCustomerOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      message.error(t('fetchError'));
    }
  }, [customerId, t]);

  const fetchOrderDetails = useCallback(async (targetOrderId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/orders/${targetOrderId}`);
      setOrder(response.data);
      settotalWeightOrder(response.data.total_weight || 0); 
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order details:', error);
      message.error(t('fetchError'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCustomerOrders();
  }, [fetchCustomerOrders]);

  useEffect(() => {
    if (customerOrders.length > 0) {
      const index = customerOrders.findIndex(order => order.id.toString() === orderId);
      setCurrentOrderIndex(index);
      fetchOrderDetails(orderId);
    }
  }, [orderId, customerOrders, fetchOrderDetails]);

  const handleNavigateOrder = (direction) => {
    const newIndex = direction === 'next' ? currentOrderIndex + 1 : currentOrderIndex - 1;
    if (newIndex >= 0 && newIndex < customerOrders.length) {
      navigate(`/customer/${customerId}/order/${customerOrders[newIndex].id}`);
    }
  };

  if (loading || !order) {
    return <div>Loading...</div>;
  }

  // Calculate total order weight from products (after order is loaded)
  const totalOrderWeight = totalWeightOrder;

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f0f2f5',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)}>
              {t('back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              #{order.id}
            </Title>
          </Space>
          <Space>
            <Button 
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => handleNavigateOrder('next')}
              disabled={currentOrderIndex >= customerOrders.length - 1}
            >
              {t('داواکاریی داهاتوو')}
            </Button>
            <Button 
              type="primary"
              icon={<ArrowLeftOutlined />} 
              onClick={() => handleNavigateOrder('prev')}
              disabled={currentOrderIndex <= 0}
            >
              {t('داواکاریی پێشوو')}
            </Button>
          </Space>
        </div>
      </Card>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={4}>
          <Card style={{ background: '#e6f7ff', borderRadius: '10px' }}>
            <Statistic
              title={t('کۆی گشتی بڕی')}
              value={formatCurrency(order.total_price)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#f6ffed', borderRadius: '10px' }}>
            <Statistic
              title={t('کۆی گشتی پارەی پێدراوە')}
              value={formatCurrency(order.amount_paid)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#fff1f0', borderRadius: '10px' }}>
            <Statistic
              title={t('باڵانسی نەدراو')}
              value={formatCurrency(order.outstanding_balance)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#fffbe6', borderRadius: '10px' }}>
            <Statistic
              title={t('کێشی داواکاری')}
              value={`${totalOrderWeight} kg`}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#fff7e6', borderRadius: '10px' }}>
            <Statistic
              title={t('بەرواری داواکاری')}
              value={(() => {
                if (!order.created_at) return '';
                return dayjs(order.created_at).utc().format('DD/MM/YYYY HH:mm:ss'); // Format as DD/MM/YYYY HH:mm:ss in UTC
              })()}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Order details content will go here */}
      <Card
        style={{
          marginTop: '24px',
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={() => navigate(`/TableOrder/InfoOrder/${order.id}`)}
          >
            {t('View Full Order Info')}
          </Button>
        </div>
        <Title level={4} style={{ marginBottom: 16 }}>{t('products Ordered')}</Title>
        <Table
          dataSource={order.order_products || []}
          rowKey={(record, idx) => record.id || idx}
          pagination={false}
          bordered
          columns={[
            {
              title: t('total'),
              dataIndex: 'total_price',
              key: 'total',
              render: (total) => {
                return total ? formatCurrency(total) : '-';
              },
            },
            {
              title: t('price'),
              dataIndex: 'single_price',
              key: 'price',
              render: (price) => {
                return price ? formatCurrency(price) : '-';
              },
            },
            {
              title: t('total Weight'),
              key: 'totalProductWeight',
              render: (record) => {
                const weight = parseFloat(record.product_weight) || 0;
                const qty = parseFloat(record.quantity) || 0;
                const total = weight * qty;
                return total ? `${total} kg` : '-';
              },
            },
            {
              title: t('weight'),
              dataIndex: 'product_weight',
              key: 'product_weight',
              render: (weight) => weight ? `${weight} kg` : '-',
            },
            {
              title: t('quantity'),
              dataIndex: 'quantity',
              key: 'quantity',
            },
            {
              title: t('productName'),
              dataIndex: 'product',
              key: 'productName',
              render: (product) => {
                if (!product) return "N/A";
                let nameObj = {};
                if (typeof product.name === "string") {
                  try {
                    nameObj = JSON.parse(product.name);
                  } catch (e) {
                    nameObj = { en: product.name };
                  }
                } else if (typeof product.name === "object" && product.name !== null) {
                  nameObj = product.name;
                } else {
                  nameObj = { en: "N/A" };
                }
                const { i18n } = require("react-i18next");
                const lang = i18n?.language || "en";
                return (
                  nameObj[lang] ||
                  nameObj.en ||
                  Object.values(nameObj).find(Boolean) ||
                  "N/A"
                );
              },
            },
          ]}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default CustomerOrderDetail;

